import React from 'react';
import ReactDOM from 'react-dom';
import { useTheme, type Theme } from '../../contexts/ThemeContext';
import { BrandLogo } from '../../components/BrandLogo';
import { NavMegaMenu } from './NavMegaMenu';
import { UserMenu, type UserMenuItem } from './UserMenu';
import { useBrandingContext } from '../../contexts/BrandingContext';
import type { ResolvedMenuGroup } from '../../plugins/types';
import type { LauncherSearchItem } from '../launcher/types';
import { ExperienceIcon, isRegistryKey } from '../icons';
import {
  isDedicatedBrandTheme,
  isTextOnlyBrandTheme,
  usesEmpowerNowHostBar,
  usesLightTextHeader,
} from '../theme/brandOwnership';
import { Moon, Sun, Waves } from 'lucide-react';

const isMacOS = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.userAgent);

interface NavItem {
  key: string;
  label: string;
  path?: string;
  icon?: string;
  children?: Array<{key: string; label: string; path: string; icon?: string}>;  // For grouped items
}

interface BrandConfig {
  logo: string;
  title: string;
  href: string;
}

// Separate component for dropdown to avoid hooks-in-loop issue
function NavDropdown({ item, onItemClick }: { item: NavItem; onItemClick?: (pluginId: string) => void }) {
  const [isOpen, setIsOpen] = React.useState(false);
  const buttonRef = React.useRef<HTMLDivElement>(null);
  const dropdownRef = React.useRef<HTMLDivElement>(null);
  const [dropdownStyle, setDropdownStyle] = React.useState<any>({});
  
  // Close dropdown when clicking outside
  React.useEffect(() => {
    if (!isOpen) return;
    
    function handleClickOutside(event: MouseEvent) {
      const target = event.target as Node;
      // Check if click is outside both button and dropdown
      if (
        buttonRef.current && !buttonRef.current.contains(target) &&
        dropdownRef.current && !dropdownRef.current.contains(target)
      ) {
        setIsOpen(false);
      }
    }
    
    // Add listener after a brief delay to avoid catching the opening click
    const timer = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside);
    }, 100);
    
    return () => {
      clearTimeout(timer);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);
  
  // Render dropdown menu
  const renderDropdownMenu = () => {
    if (!isOpen || !item.children || item.children.length === 0) {
      return null;
    }

    const dropdownContent = (
      <div
        ref={dropdownRef}
        className="nav-dropdown-menu"
        style={{
          position: 'fixed',
          ...dropdownStyle,
          minWidth: '200px',
          background: 'var(--dropdown-bg)',
          border: '1px solid var(--dropdown-border)',
          borderRadius: '8px',
          boxShadow: 'var(--card-shadow)',
          padding: '8px 0',
          zIndex: 10000,
          display: 'block',
          visibility: 'visible',
          opacity: 1,
        }}
      >
        {item.children.map((child, idx) => (
          <a
            key={child.key || idx}
            href={child.path}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              console.log(`Navigating to: ${child.path}`);
              setIsOpen(false);
              // Track plugin usage
              if (onItemClick) {
                const pluginId = child.path.replace(/^\//, '').split('/')[0];
                if (pluginId) {
                  onItemClick(pluginId);
                }
              }
              // Use a small delay to ensure state updates before navigation
              setTimeout(() => {
                window.location.href = child.path;
              }, 50);
            }}
            style={{
              display: 'block',
              padding: '12px 20px',
              color: 'var(--dropdown-text, var(--main-text))',
              textDecoration: 'none',
              fontSize: '14px',
              fontWeight: 500,
              transition: 'all 0.15s cubic-bezier(0.4, 0, 0.2, 1)',
              borderLeft: '3px solid transparent',
              background: 'transparent',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'var(--dropdown-item-hover, var(--table-row-hover))';
              e.currentTarget.style.borderLeftColor = 'var(--dropdown-item-hover-border, var(--main-accent))';
              e.currentTarget.style.color = 'var(--main-accent)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'transparent';
              e.currentTarget.style.borderLeftColor = 'transparent';
              e.currentTarget.style.color = 'var(--dropdown-text, var(--main-text))';
            }}
          >
            {child.label}
          </a>
        ))}
      </div>
    );

    // Render to document.body using portal to escape stacking context
    return ReactDOM.createPortal(dropdownContent, document.body);
  };

  return (
    <div ref={buttonRef} style={{ position: 'relative' }}>
      <button
        onClick={(e) => {
          e.stopPropagation();
          e.preventDefault();
          const newState = !isOpen;
          
          // Calculate dropdown position relative to button
          if (newState && buttonRef.current) {
            const rect = buttonRef.current.getBoundingClientRect();
            setDropdownStyle({
              left: `${rect.left}px`,
              top: `${rect.bottom + 6}px`,
            });
          }
          
          setIsOpen(newState);
        }}
        style={{
          padding: '6px 10px',
          color: 'var(--header-text, #ffffff)',
          textDecoration: 'none',
          borderRadius: 6,
          fontSize: 13,
          fontWeight: 400,
          background: 'transparent',
          border: '1px solid transparent',
          transition: 'all 0.2s ease',
          whiteSpace: 'nowrap',
          display: 'inline-flex',
          alignItems: 'center',
          gap: 4,
          cursor: 'pointer',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.background = 'rgba(255,255,255,0.1)';
          e.currentTarget.style.borderColor = 'rgba(255,255,255,0.2)';
          e.currentTarget.style.color = 'var(--header-text, #ffffff)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = 'transparent';
          e.currentTarget.style.borderColor = 'transparent';
          e.currentTarget.style.color = 'var(--header-text, #ffffff)';
        }}
      >
        {item.icon && isRegistryKey(item.icon) && <ExperienceIcon iconKey={item.icon} size={16} strokeWidth={1.5} />}
        {item.label} <svg width="10" height="6" viewBox="0 0 10 6" fill="none" style={{ opacity: 0.6, marginLeft: 2 }}><path d="M1 1L5 5L9 1" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
      </button>
      
      {/* Dropdown Menu - rendered via portal to document.body */}
      {renderDropdownMenu()}
    </div>
  );
}

interface CustomHeaderNavProps {
  items: NavItem[];
  brand: BrandConfig;
  userName: string;
  onLogout: () => void;
  showThemeToggle?: boolean;
  showSearch?: boolean;
  /** Mega menu groups (takes priority over items for matching groups) */
  megaMenuGroups?: ResolvedMenuGroup[];
  /** Recent plugin IDs for mega menu */
  recentPluginIds?: string[];
  /** Callback when a plugin is clicked (for tracking usage) */
  onPluginClick?: (pluginId: string) => void;
  /** Whether mega menu data is still loading */
  megaMenuLoading?: boolean;
  /** Open the global launcher */
  onLauncherOpen?: () => void;
  /** Ref for the launcher trigger button (focus return) */
  launcherTriggerRef?: React.RefObject<HTMLButtonElement | null>;
  /** Global launcher search items for mega menu cross-search */
  globalSearchItems?: LauncherSearchItem[];
  /** Slot for header widgets (Guide Me, plugin-contributed widgets) rendered before the launcher pill */
  headerSlots?: React.ReactNode;
}

export function CustomHeaderNav({
  items,
  brand,
  userName,
  onLogout,
  showThemeToggle = false,
  showSearch = false,
  megaMenuGroups,
  recentPluginIds = [],
  onPluginClick,
  megaMenuLoading = false,
  onLauncherOpen,
  launcherTriggerRef,
  globalSearchItems,
  headerSlots,
}: CustomHeaderNavProps) {
  const { theme, setTheme } = useTheme();
  const { config: branding } = useBrandingContext();
  const [searchQuery, setSearchQuery] = React.useState('');

  const userMenuItems = React.useMemo((): UserMenuItem[] => {
    const menuItems: UserMenuItem[] = [];
    const hideAppCenter = branding?.header?.hideAppCenter === true;
    const hideMySecurity =
      branding?.header?.hideMySecurity === true ||
      hideAppCenter ||
      branding?.appName === 'NomadID' ||
      branding?.shortName === 'NID';

    if (!hideAppCenter) {
      menuItems.push({
        id: 'app-center',
        label: 'App Center',
        href: '/app-center',
        testId: 'experience-user-menu-app-center',
      });
    }
    if (!hideMySecurity) {
      menuItems.push({
        id: 'my-security',
        label: 'My Security',
        href: '/identity-core/my-security',
        testId: 'experience-user-menu-my-security',
      });
    }
    return menuItems;
  }, [
    branding?.header?.hideAppCenter,
    branding?.header?.hideMySecurity,
    branding?.appName,
    branding?.shortName,
  ]);
  
  // Create a map of group names to mega menu groups for quick lookup
  // SECURITY: Only include groups that have accessible plugins
  const megaMenuGroupMap = React.useMemo(() => {
    const map = new Map<string, ResolvedMenuGroup>();
    if (megaMenuGroups) {
      for (const group of megaMenuGroups) {
        // Only add groups with items the user can access
        const hasItems = group.type === 'mega' && group.columns
          ? group.columns.some(col => col.items && col.items.length > 0)
          : group.type === 'dropdown' && group.items && group.items.length > 0;
        
        if (hasItems) {
          map.set(group.group, group);
        }
      }
    }
    return map;
  }, [megaMenuGroups]);
  
  // Handle plugin click for tracking
  const handlePluginClick = React.useCallback((pluginId: string) => {
    if (onPluginClick) {
      onPluginClick(pluginId);
    }
  }, [onPluginClick]);
  
  // Theme toggle helper
  const toggleTheme = () => {
    const themes = ['blue', 'white', 'black'];
    const currentIndex = themes.indexOf(theme);
    const nextIndex = (currentIndex + 1) % themes.length;
    setTheme(themes[nextIndex] as Theme);  // Type assertion for new Theme type
  };


  // Determine if current path matches item
  const isActive = (path: string) => {
    if (typeof window === 'undefined') return false;
    return window.location.pathname === path || window.location.pathname.startsWith(path + '/');
  };

  // The header "wears the brand that owns the bar". Dark-header platform themes get
  // the navy host bar + endorsed EmpowerID parent lockup; Clean Light and dedicated
  // product themes use a single lockup from themes.yaml branding; customer
  // white-label themes suppress the parent (see brandOwnership allow-list).
  const dedicatedBrand = isDedicatedBrandTheme(theme);
  const textOnlyBrand = isTextOnlyBrandTheme(theme);
  const brandOwnsBar = usesEmpowerNowHostBar(theme);
  const showEndorsedParent = brandOwnsBar;
  const headerLogoVariant = dedicatedBrand
    ? (usesLightTextHeader(theme) ? 'dark' : 'light')
    : brandOwnsBar
      ? 'dark'
      : 'primary';
  // Dimmer chrome on the navy bar must still clear AA — bump the separator/suffix
  // opacity so white-at-alpha over #0a2a52 stays legible.
  const dividerOpacity = brandOwnsBar ? 0.6 : 0.4;

  return (
    <nav
      className="custom-header-nav"
      aria-label="Primary"
      data-testid="experience-nav"
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '0 20px',
        height: 64,
        background: 'var(--header-bg)',
        WebkitBackdropFilter: 'var(--header-backdrop-filter, saturate(1.1) blur(6px))',
        backdropFilter: 'var(--header-backdrop-filter, saturate(1.1) blur(6px))',
        borderBottom: 'var(--header-border-bottom, 1px solid var(--border-color))',
        boxShadow: 'var(--header-shadow, none)',
        position: 'sticky',
        top: 0,
        zIndex: 50,
      }}
    >
      {/* Brand Section — dedicated product lockup OR endorsed-hybrid: empowerID / EmpowerID Identity Fabric / title */}
      <div data-testid="experience-brand" style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
        <a
          href={brand.href}
          aria-label={
            dedicatedBrand || textOnlyBrand
              ? `${brand.title} Home`
              : `${showEndorsedParent ? 'EmpowerID, ' : ''}${brand.title} Home`
          }
          data-testid="experience-brand-logo"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 8,
            textDecoration: 'none',
          }}
        >
          {textOnlyBrand ? (
            <span
              role="heading"
              aria-level={1}
              data-testid="experience-brand-wordmark"
              style={{
                fontFamily: "var(--font-display, 'Space Grotesk', system-ui, sans-serif)",
                fontSize: 18,
                fontWeight: 700,
                color: 'var(--header-text, var(--main-text))',
                letterSpacing: '-0.01em',
                lineHeight: 1.1,
                whiteSpace: 'nowrap',
              }}
            >
              {brand.title}
            </span>
          ) : (
            <>
              {showEndorsedParent && (
                <>
                  <span
                    data-testid="experience-brand-parent"
                    style={{
                      fontFamily: "var(--font-display, 'Space Grotesk', system-ui, sans-serif)",
                      fontSize: 16,
                      fontWeight: 700,
                      color: 'var(--header-text, #ffffff)',
                      letterSpacing: '-0.01em',
                      lineHeight: 1.1,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    empower<span style={{ color: 'var(--brand-id-accent, #4FD8E8)' }}>ID</span>
                  </span>
                  <span aria-hidden="true" style={{ color: 'var(--header-text, #ffffff)', opacity: dividerOpacity, fontSize: 15, fontWeight: 300 }}>／</span>
                </>
              )}
              <BrandLogo
                height={dedicatedBrand ? 32 : 26}
                variant={headerLogoVariant}
                alt=""
                style={{ filter: dedicatedBrand ? 'none' : ('var(--header-logo-filter, none)' as React.CSSProperties['filter']) }}
              />
            </>
          )}
        </a>
        {!dedicatedBrand && !textOnlyBrand && (
          <>
            <span aria-hidden="true" style={{ color: 'var(--header-text, #ffffff)', opacity: dividerOpacity, fontSize: 15, fontWeight: 300 }}>／</span>
            <span
              role="heading"
              aria-level={1}
              style={{
                fontSize: 15,
                fontWeight: 400,
                color: 'var(--header-text, rgba(255,255,255,0.7))',
                opacity: 0.7,
                letterSpacing: '0.01em',
                lineHeight: 1.1,
                whiteSpace: 'nowrap',
              }}
            >
              {brand.title}
            </span>
          </>
        )}
      </div>

      {/* Separator between brand and nav */}
      <div style={{ width: 1, height: 24, background: 'var(--header-text, rgba(255,255,255,0.15))', opacity: brandOwnsBar ? 0.28 : 0.15, flexShrink: 0, margin: '0 8px' }} />

      {/* Navigation Items */}
      <div
        data-testid="experience-nav-items"
        style={{
          display: 'flex',
          gap: 2,
          alignItems: 'center',
          flex: 1,
          minWidth: 0,
          marginRight: 8,
          overflow: 'hidden',
        }}
      >
        {/* First render mega menu groups if available */}
        {/* SECURITY: Filter out empty groups (no accessible plugins) */}
        {megaMenuGroups && megaMenuGroups
          .filter(group => {
            // Check if group has any items the user can access
            if (group.type === 'mega' && group.columns) {
              return group.columns.some(col => col.items && col.items.length > 0);
            }
            if (group.type === 'dropdown' && group.items) {
              return group.items.length > 0;
            }
            return false;
          })
          .map((group) => (
            <NavMegaMenu
              key={`mega:${group.group}`}
              group={group}
              recentPluginIds={recentPluginIds}
              onPluginClick={handlePluginClick}
              loading={megaMenuLoading}
              globalSearchItems={globalSearchItems}
            />
          ))}
        
        {/* Render legacy items that don't have a matching mega menu group */}
        {items.map((item) => {
          // Skip if this item matches a mega menu group (mega menu takes priority)
          if (megaMenuGroups && megaMenuGroupMap.has(item.label)) {
            return null;
          }
          
          // Grouped item (has children) - render as dropdown component
          if (item.children && item.children.length > 0) {
            return <NavDropdown key={item.key} item={item} onItemClick={handlePluginClick} />;
          }
          
          // Regular item (no children) - render as direct link
          const active = item.path ? isActive(item.path) : false;
          return (
            <a
              key={item.key}
              href={item.path || '#'}
              aria-current={active ? 'page' : undefined}
              onClick={(e) => {
                if (item.path) {
                  // Extract plugin ID from path (remove leading slash)
                  const pluginId = item.path.replace(/^\//, '').split('/')[0];
                  if (pluginId) {
                    handlePluginClick(pluginId);
                  }
                }
              }}
              style={{
                position: 'relative',
                padding: '6px 10px',
                color: active ? 'var(--header-text, #ffffff)' : 'var(--header-text, rgba(255,255,255,0.9))',
                textDecoration: 'none',
                borderRadius: 6,
                fontSize: 13,
                fontWeight: active ? 600 : 400,
                background: active ? 'rgba(255,255,255,0.15)' : 'transparent',
                border: active ? '1px solid rgba(255,255,255,0.3)' : '1px solid transparent',
                transition: 'all 0.2s ease',
                whiteSpace: 'nowrap',
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
              }}
              onMouseEnter={(e) => {
                if (!active) {
                  e.currentTarget.style.background = 'rgba(255,255,255,0.1)';
                  e.currentTarget.style.borderColor = 'rgba(255,255,255,0.2)';
                  e.currentTarget.style.color = 'var(--header-text, #ffffff)';
                }
              }}
              onMouseLeave={(e) => {
                if (!active) {
                  e.currentTarget.style.background = 'transparent';
                  e.currentTarget.style.borderColor = 'transparent';
                  e.currentTarget.style.color = 'var(--header-text, rgba(255,255,255,0.9))';
                }
              }}
            >
              {item.icon && isRegistryKey(item.icon) && <ExperienceIcon iconKey={item.icon} size={16} strokeWidth={1.5} />}
              {item.label}
              {active && (
                <span style={{
                  position: 'absolute',
                  left: 0,
                  right: 0,
                  bottom: -13,
                  height: 2,
                  background: 'var(--quantum-violet, #6C4CFF)',
                  borderRadius: 1,
                }} />
              )}
            </a>
          );
        })}
      </div>

      {/* Right Section: Slots, Launcher, Theme Toggle, User Menu */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0, marginLeft: 8 }}>
        {/* Header slots (Guide Me, plugin widgets) */}
        {headerSlots}

        {/* Global Launcher Trigger */}
        {onLauncherOpen && (
          <button
            ref={launcherTriggerRef}
            onClick={onLauncherOpen}
            data-testid="global-launcher-trigger"
            aria-label={`Search or jump to anything (${isMacOS ? '\u2318K' : 'Ctrl+K'})`}
            className="launcher-pill"
          >
            <svg className="launcher-pill-icon" width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true">
              <circle cx="7" cy="7" r="5.5" stroke="currentColor" strokeWidth="1.5"/>
              <path d="M11.5 11.5L14.5 14.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
            </svg>
            {/* color:inherit escapes the broad `[data-theme] span` rule so the pill label
                follows the pill's --header-text (white on the navy host bar). */}
            <span className="launcher-pill-text" style={{ color: 'inherit' }}>Search or jump to...</span>
            <kbd className="launcher-pill-kbd">
              {isMacOS ? '\u2318K' : 'Ctrl K'}
            </kbd>
          </button>
        )}

        {/* Search (optional - legacy) */}
        {showSearch && (
          <div style={{ position: 'relative' }}>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search..."
              style={{
                padding: '6px 12px',
                background: 'var(--search-bg)',
                border: '1px solid var(--search-border)',
                borderRadius: 6,
                color: 'var(--input-text)',
                fontSize: 13,
                outline: 'none',
                width: 200,
              }}
            />
          </div>
        )}

        {/* Theme Toggle (optional) */}
        {showThemeToggle && (
          <button
            onClick={toggleTheme}
            style={{
              padding: '6px 10px',
              background: 'var(--input-bg)',
              border: '1px solid var(--input-border)',
              borderRadius: 6,
              color: 'var(--main-text)',
              cursor: 'pointer',
              lineHeight: 0,
              display: 'inline-flex',
              alignItems: 'center',
            }}
            title="Toggle theme"
            aria-label="Toggle theme"
          >
            {theme === 'black' ? (
              <Moon size={18} strokeWidth={1.8} aria-hidden="true" />
            ) : theme === 'white' ? (
              <Sun size={18} strokeWidth={1.8} aria-hidden="true" />
            ) : (
              <Waves size={18} strokeWidth={1.8} aria-hidden="true" />
            )}
          </button>
        )}

        {/* User Menu */}
        <UserMenu
          userName={userName}
          items={userMenuItems}
          onLogout={onLogout}
        />
      </div>
    </nav>
  );
}

